/**
 * Shared helper for deduplicating friends by email
 * Consolidates multiple entries with same email
 * Priority: accepted > pending
 */

import type { Friend } from '../types/friends.types'

/**
 * Deduplicate friends by email - consolidate multiple entries with same email
 * Priority: accepted > pending
 * If both are pending, prefer the most recent one (by invitation date)
 */
export const deduplicateFriendsByEmail = (friends: Friend[]): Friend[] => {
  const emailMap = new Map<string, Friend>()

  friends.forEach((friend) => {
    // Skip if no email (shouldn't happen for pending invitations, but be safe)
    if (!friend.email) {
      // If no email, use the ID as the key (for accepted friends without email)
      if (!emailMap.has(friend.id)) {
        emailMap.set(friend.id, friend)
      }
      return
    }

    const emailKey = friend.email.toLowerCase().trim()
    const existing = emailMap.get(emailKey)

    if (!existing) {
      // First entry with this email
      emailMap.set(emailKey, friend)
    } else {
      // Duplicate email found - consolidate
      // Priority: accepted > pending
      // If both are pending, prefer the most recent one (by invitation date or creation)
      if (existing.friendshipStatus === 'accepted' && friend.friendshipStatus === 'pending') {
        // Keep the accepted friend, ignore pending invitation
      } else if (existing.friendshipStatus === 'pending' && friend.friendshipStatus === 'accepted') {
        // Replace pending with accepted
        emailMap.set(emailKey, friend)
      } else if (existing.friendshipStatus === 'pending' && friend.friendshipStatus === 'pending') {
        // Both are pending - prefer the most recent one
        const existingDate = existing.invitedAt ? new Date(existing.invitedAt) : null
        const friendDate = friend.invitedAt ? new Date(friend.invitedAt) : null

        if (friendDate && existingDate && friendDate > existingDate) {
          // Friend is more recent, replace
          emailMap.set(emailKey, friend)
        }
      }
      // Both are accepted (shouldn't happen, but keep the first one)
    }
  })

  return Array.from(emailMap.values())
}

